// SUB-OFFICE 7 — THE VERDICT SLIP. Line-by-line at a dignified rate (~90ms
// per line in the full ceremony, 30ms expedited), four rubric rows with the
// server's band-constrained decimals, a memo whose quoted phrases are the
// petitioner's exact characters, exactly one server telemetry line plus the
// movement observation measured during THIS wait (deterministic grammar,
// re-derived server-side at /api/ack — the Bureau never trusts client text),
// then the stamp, all at once, as mechanisms arrive.
//
// DENIED bands offer [FILE FORM 11-B]: the appeals overlay is the game's one
// wood-paneled room — 8 deliberate seconds even when the Clerk is faster.

import { api } from '../net';
import type { AckResponse, AppealResponse, CeremonyTier, Verdict } from '../../shared/types';
import { holdSlamButton, memoWithQuotes, printLayer, redacted, slamStamp, stampEl } from './chrome';
import { hashJitter } from '../../shared/rng';
import type { EventBus } from '../entities/entities';

/** Display decimal (hashJitter convention). The server SHOULD run displayScore,
 *  but any subscore that arrives as a bare integer (e.g. grovel:1) is given the
 *  worker-identical stable jitter here so a rubric row never renders a
 *  suspiciously round "6" where "6.4" belongs. Already-fractional values pass
 *  through untouched — no double-jitter. */
function displayScore(sub: number, verdictId: string, axis: string): number {
  if (!Number.isInteger(sub)) return sub;
  return Math.min(9.9, Math.max(0, sub) + hashJitter(verdictId, axis));
}

export interface SlipOptions {
  tier: CeremonyTier;
  movedM: number;
  stoodStill: boolean;
  designation: string;
  /** live overhead-label tick while the standing line prints */
  onStandingTick: (standing: number) => void;
  onSlamPop: () => void;
}

export interface SlipOutcome {
  ack: AckResponse | null;
  appeal: AppealResponse | null;
}

const DENIED_TONES = new Set(['DENIED', 'DENIED WITH PREJUDICE', 'UNFIT TRIBUTE', 'PETITION DENIED', 'CEASED BY REQUEST']);

/** The movement observation grammar (numeric bands; server re-derives). */
export function movementLine(movedM: number, stoodStill: boolean): string {
  if (stoodStill || movedM < 0.5) {
    return 'You did not move while this offering was considered. Composure or paralysis. The Bureau will assume composure.';
  }
  const m = movedM.toFixed(1);
  if (movedM < 2) return `You shifted ${m} meters while your offering was considered. The shift has been filed.`;
  if (movedM < 8) return `You wandered ${m} meters while your offering was considered. The Bureau notes the wandering.`;
  if (movedM < 20) return `You paced ${m} meters while your offering was considered. Pacing has never once helped.`;
  return `You walked ${m} meters while your offering was considered. The Bureau notes restlessness.`;
}

export class VerdictSlipUi {
  constructor(private bus: EventBus) {}

  // -------------------------------------------------------------------------

  async show(verdict: Verdict, opts: SlipOptions): Promise<SlipOutcome> {
    const layer = printLayer();
    const slip = document.createElement('div');
    slip.className = 'slip';
    layer.appendChild(slip);

    const lineRate = opts.tier === 'EXPEDITED' ? 30 : 90;
    const lines: { text: string; cls?: string; frag?: DocumentFragment }[] = [];
    const push = (text: string, cls?: string, frag?: DocumentFragment): void => {
      lines.push({ text, cls, frag });
    };

    push('BUREAU OF SATISFACTION — SUB-OFFICE 7', 'letterhead');
    push(`VERDICT ${verdict.verdict_id}   FORM: ${verdict.kind}`);
    push(`PETITIONER: ${opts.designation}`);
    push('', 'rule-line');
    if (verdict.scores) {
      const vid = verdict.verdict_id;
      push(`SINCERITY${dots('SINCERITY')}${fmt(displayScore(verdict.scores.sincerity, vid, 'sincerity'))}`, 'rubric-line');
      push(`EFFORT${dots('EFFORT')}${fmt(displayScore(verdict.scores.effort, vid, 'effort'))}`, 'rubric-line');
      push(`GROVEL${dots('GROVEL')}${fmt(displayScore(verdict.scores.grovel, vid, 'grovel'))}`, 'rubric-line');
      push(`ADHERENCE${dots('ADHERENCE')}${fmt(displayScore(verdict.scores.adherence, vid, 'adherence'))}`, 'rubric-line');
      push('', 'rule-line');
    }
    // CITED AS PRECEDENT — its own section before the memo. The excerpt carries
    // [[R]] markers; render them as redaction bars exactly like every other
    // excerpt (reuse `redacted`). String-injected verbatim from D1 by the server.
    if (verdict.cited_precedent) {
      const cp = verdict.cited_precedent;
      const frag = document.createDocumentFragment();
      frag.appendChild(document.createTextNode(`PRECEDENT CITED: ${cp.designation} — quote, `));
      frag.appendChild(redacted(cp.excerpt));
      frag.appendChild(document.createTextNode(', unquote.'));
      push(`PRECEDENT CITED: ${cp.designation} — quote, ${cp.excerpt}, unquote.`, 'cited-line', frag);
      push('', 'rule-line');
    }
    push('MEMO:', 'memo-label');
    push(verdict.memo, 'memo-block', memoWithQuotes(verdict.memo, verdict.quotes || []));
    if (verdict.telemetry_line) {
      push('', 'rule-line');
      push(verdict.telemetry_line);
    }
    push(movementLine(opts.movedM, opts.stoodStill));
    for (const f of verdict.constraint_failures || []) {
      push(`CONSTRAINT VIOLATION: ${f}`);
    }
    push('', 'rule-line');
    const deltaStr = verdict.standing_delta > 0 ? `+${verdict.standing_delta}` : `${verdict.standing_delta}`;
    push(`STANDING ${deltaStr} → ${verdict.standing}   GRADE: ${verdict.grade}`, 'standing-line');
    if (verdict.stamp_flavor) push(verdict.stamp_flavor);
    if (verdict.sealed) push('SEALED BY ORDER OF DEPARTMENT 2.');
    else if (verdict.published) push('ENTERED INTO THE PUBLIC RECORD.');

    // print
    const els: HTMLElement[] = [];
    for (const l of lines) {
      const div = document.createElement('div');
      div.className = 'line' + (l.cls ? ' ' + l.cls : '');
      if (l.cls !== 'rule-line') {
        if (l.frag) div.appendChild(l.frag);
        else div.textContent = l.text;
        // carbon-ribbon soul: slight per-line ink variance and baseline jitter
        div.style.opacity = '0';
        div.style.transform = `translateY(${(Math.random() - 0.5).toFixed(2)}px)`;
      }
      slip.appendChild(div);
      els.push(div);
    }
    const stampZone = document.createElement('div');
    stampZone.className = 'slip-stamp-zone';
    slip.appendChild(stampZone);
    const actions = document.createElement('div');
    actions.className = 'slip-actions';
    slip.appendChild(actions);

    const oldStanding = verdict.standing - verdict.standing_delta;
    await new Promise<void>((resolve) => {
      let i = 0;
      const timer = window.setInterval(() => {
        if (i >= els.length) {
          window.clearInterval(timer);
          resolve();
          return;
        }
        const div = els[i];
        div.classList.add('printed');
        div.style.opacity = String(0.92 + Math.random() * 0.08);
        this.bus.emit('audio:tick');
        // the standing counterweight ticks the overhead label live
        if (div.classList.contains('standing-line') && verdict.standing_delta !== 0) {
          this.tickStanding(oldStanding, verdict.standing, opts.onStandingTick);
        }
        i++;
      }, lineRate);
    });

    // THE STAMP — 3-frame anticipation upstream, 80ms arrival, all at once
    const tone = verdict.clemency || verdict.stamp.includes('CLEMENCY')
      ? 'blue'
      : DENIED_TONES.has(verdict.stamp) ? 'red' : 'ink';
    await new Promise<void>((resolve) => {
      window.setTimeout(() => {
        slamStamp(stampZone, stampEl(verdict.stamp, tone), () => {
          this.bus.emit('audio:thunk');
          opts.onSlamPop();
          resolve();
        });
      }, opts.tier === 'EXPEDITED' ? 120 : 420);
    });

    for (const s of verdict.new_stamps || []) {
      this.bus.emit('toast', `STAMP AFFIXED TO YOUR FILE: ${s}`);
    }

    // ---- consequence -------------------------------------------------------
    return new Promise<SlipOutcome>((resolve) => {
      let appealResult: AppealResponse | null = null;

      const finish = async (): Promise<void> => {
        const res = await api.ack(verdict.verdict_id, opts.movedM, opts.stoodStill);
        slip.remove();
        const ack = res.data;
        if (ack && ack.observation) this.bus.emit('chyron', ack.observation);
        if (ack && ack.pending_verdicts && ack.pending_verdicts.length) {
          this.bus.emit('toast', 'A QUEUED VERDICT HAS FOUND YOU. VERDICTS ALWAYS DO.');
        }
        for (const dv of ack?.deferred_verdicts || []) {
          this.bus.emit('toast', `FROM A ROOM THAT IS NOT IN USE — ${dv.stamp}: ${dv.memo}`);
        }
        resolve({ ack, appeal: appealResult });
      };

      if (opts.tier === 'EXPEDITED') {
        // familiarity: a single tap acknowledges; the Bureau has stopped performing
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'slam-btn';
        btn.textContent = 'ACKNOWLEDGE';
        btn.addEventListener('click', () => void finish());
        actions.appendChild(btn);
      } else {
        actions.appendChild(
          holdSlamButton('ACKNOWLEDGE', () => void finish())
        );
      }

      if (verdict.appealable) {
        const appealBtn = document.createElement('button');
        appealBtn.type = 'button';
        appealBtn.className = 'slam-btn secondary red';
        appealBtn.textContent = 'FILE FORM 11-B';
        appealBtn.addEventListener('click', () => {
          void (async () => {
            appealBtn.disabled = true;
            appealResult = await this.appealFlow(verdict);
            if (appealResult) appealBtn.remove();
            else appealBtn.disabled = false;
          })();
        });
        actions.appendChild(appealBtn);
      }
    });
  }

  private tickStanding(from: number, to: number, cb: (s: number) => void): void {
    const steps = Math.abs(to - from);
    if (steps === 0) return;
    const dir = Math.sign(to - from);
    let cur = from;
    const t = window.setInterval(() => {
      cur += dir;
      cb(cur);
      this.bus.emit('audio:flap');
      if (cur === to) window.clearInterval(t);
    }, Math.max(60, 600 / steps));
  }

  // -------------------------------------------------------------------------
  // QUEUED chit (INDUSTRIAL ACTION / past the conversion window)
  // -------------------------------------------------------------------------

  showQueuedChit(memo: string): Promise<void> {
    const layer = printLayer();
    const card = document.createElement('div');
    card.className = 'paper-modal chit';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>PROCESSING CHIT</span><span class="tab-num">FORM Q-11</span></div>`;
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    body.textContent = memo;
    const note = document.createElement('div');
    note.className = 'compose-note';
    note.style.marginTop = '10px';
    note.textContent = 'YOUR VERDICT WILL BE DELIVERED TO YOUR DRAWER. THE DRAWER KNOWS WHERE YOU ARE.';
    body.appendChild(note);
    card.appendChild(body);
    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    card.appendChild(foot);
    layer.appendChild(card);
    return new Promise((resolve) => {
      foot.appendChild(
        holdSlamButton('TAKE THE CHIT', () => {
          this.bus.emit('audio:thunk');
          card.remove();
          resolve();
        })
      );
    });
  }

  // -------------------------------------------------------------------------
  // APPEALS — Form 11-B; fixed 8s deliberation; both outcomes are ceremonies
  // -------------------------------------------------------------------------

  private appealFlow(verdict: Verdict): Promise<AppealResponse | null> {
    const layer = printLayer();
    const veneer = document.createElement('div');
    veneer.className = 'appeals-veneer';
    layer.appendChild(veneer);
    const card = document.createElement('div');
    card.className = 'appeals-card';
    layer.appendChild(card);

    const head = document.createElement('div');
    head.innerHTML =
      `<div class="lh-title letterhead">OFFICE OF APPEALS</div>` +
      `<div class="lh-sub letterhead">FORM 11-B — PETITION FOR RECONSIDERATION. ONE PER VERDICT. EVER.</div>` +
      `<hr class="rule">`;
    card.appendChild(head);
    const original = document.createElement('div');
    original.style.opacity = '0.75';
    original.style.fontSize = '12px';
    original.appendChild(redacted(`ORIGINAL FINDING: ${verdict.stamp}. ${verdict.memo}`));
    card.appendChild(original);
    card.appendChild(document.createElement('hr')).className = 'rule';

    const area = document.createElement('textarea');
    area.maxLength = 1200;
    area.spellcheck = false;
    area.placeholder = 'State, without embellishment, why the finding is wrong. I will read it. I read all of them...';
    card.appendChild(area);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    foot.style.borderTop = 'none';
    card.appendChild(foot);

    return new Promise((resolve) => {
      const close = (r: AppealResponse | null): void => {
        veneer.remove();
        card.remove();
        resolve(r);
      };
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'slam-btn secondary';
      back.textContent = 'WITHDRAW';
      back.addEventListener('click', () => close(null));
      foot.appendChild(back);

      foot.appendChild(
        holdSlamButton('FILE FORM 11-B', () => {
          const text = area.value.trim();
          if (!text) {
            this.bus.emit('toast', 'A blank appeal appeals to no one.');
            return;
          }
          void (async () => {
            area.disabled = true;
            foot.style.visibility = 'hidden';
            const wait = document.createElement('div');
            wait.className = 'ellipsis-wait typewriter';
            wait.textContent = 'The Clerk is reading it . . .';
            card.appendChild(wait);
            const t0 = performance.now();
            const res = await api.appeal(verdict.verdict_id, text);
            // the deliberation is 8 seconds even when the Clerk is faster —
            // reconsideration must be seen to take time
            const left = 8000 - (performance.now() - t0);
            if (left > 0) await new Promise((r) => window.setTimeout(r, left));
            wait.remove();
            if (!res.ok || !res.data) {
              this.bus.emit('toast', res.memo || 'The Clerk has stepped away. The appeal was not consumed.');
              area.disabled = false;
              foot.style.visibility = '';
              return;
            }
            const a = res.data;
            const out = document.createElement('div');
            out.style.marginTop = '10px';
            const memoEl = document.createElement('div');
            memoEl.textContent = a.memo;
            out.appendChild(memoEl);
            const zone = document.createElement('div');
            zone.className = 'slip-stamp-zone';
            out.appendChild(zone);
            card.appendChild(out);
            slamStamp(zone, stampEl(a.stamp, a.overturn ? 'blue' : 'red'), () =>
              this.bus.emit('audio:thunk')
            );
            for (const s of a.new_stamps || []) this.bus.emit('toast', `STAMP AFFIXED TO YOUR FILE: ${s}`);
            const doneRow = document.createElement('div');
            doneRow.className = 'paper-foot';
            doneRow.style.borderTop = 'none';
            doneRow.appendChild(holdSlamButton('ACKNOWLEDGE', () => close(a)));
            card.appendChild(doneRow);
          })();
        })
      );
    });
  }
}

function fmt(n: number): string {
  return n.toFixed(1).padStart(5, ' ');
}

function dots(label: string): string {
  return ' ' + '.'.repeat(Math.max(2, 22 - label.length)) + ' ';
}
